import { createDisplayNumberFormat } from '../../utils/displayNumberFormat';
import { useId, useState } from 'react';
import { displayDigits } from '../../utils/displayDigits';
import { useTranslation } from 'react-i18next';
import type { WeekBar } from './types';
import { DONUT_PALETTE, SPORT_COLORS } from './palette';
import './SportDurationDonut.css';

// Fitness summary duration is the API's SUM(total_time), in seconds.
function duration(seconds: number) {
  const minutes = Math.round(seconds / 60);
  return displayDigits(`${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`);
}

export function SportDurationDonut({ data, maximumFractionDigits = 1 }: { data: WeekBar[]; maximumFractionDigits?: number }) {
  const { t, i18n } = useTranslation();
  const gradientId = useId().replace(/:/g, "");
  const [selected, setSelected] = useState<string | null>(null);
  const rows = data.filter(row => Number.isFinite(row.value) && row.value > 0);
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  const active = rows.find(row => row.label === selected);
  const name = (label: string) => t(`sportDuration.sports.${label.toLowerCase()}`, { defaultValue: label });
  const percent = (value: number) => createDisplayNumberFormat(i18n.language, { style: 'percent', maximumFractionDigits }).format(value / total);
  let offset = 0;
  if (!total) return <p className="sport-duration-empty">{t('sportDuration.empty')}</p>;
  return (
    <div className="sport-duration">
      <div className="sport-duration__pie">
        <svg viewBox="0 0 280 280" role="group" aria-label={t('sportDuration.title')}>
          <defs>{rows.map((row, index) => <linearGradient key={index} id={`${gradientId}-${index}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={SPORT_COLORS[row.label.toLowerCase()] ?? DONUT_PALETTE[index % DONUT_PALETTE.length]} />
            <stop offset="100%" stopColor={SPORT_COLORS[row.label.toLowerCase()] ?? DONUT_PALETTE[index % DONUT_PALETTE.length]} stopOpacity=".9" />
          </linearGradient>)}</defs>
          {rows.map((row, index) => {
            const sweep = row.value / total * Math.PI * 2;
            const gap = rows.length > 1 ? Math.min(.065, sweep * .2) : 0;
            const start = offset - Math.PI / 2 + gap / 2;
            offset += sweep;
            const end = start + sweep - gap;
            const r = 108;
            const xy = (a: number) => `${140 + r * Math.cos(a)} ${140 + r * Math.sin(a)}`;
            // Two arcs also support a single sport (a complete circle).
            const mid = (start + end) / 2;
            const path = `M ${xy(start)} A ${r} ${r} 0 0 1 ${xy(mid)} A ${r} ${r} 0 0 1 ${xy(end)}`;
            return <path key={`${row.label}-${index}`} d={path}
              fill="none" className="sport-duration__slice"
              stroke={`url(#${gradientId}-${index})`} strokeWidth="9" strokeLinecap="butt"
              role="button" tabIndex={0} aria-pressed={active?.label === row.label}
              aria-label={`${name(row.label)}: ${percent(row.value)} · ${duration(row.value)}`}
              onClick={() => setSelected(selected === row.label ? null : row.label)}
              onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelected(selected === row.label ? null : row.label); } }}>
              <title>{name(row.label)}: {percent(row.value)} · {duration(row.value)}</title>
            </path>;
          })}
        </svg>
        <div className="sport-duration__detail" aria-live="polite">
          <span>{t('sportDuration.total')}</span>
          <strong dir="ltr">{duration(total)}</strong>
          <small>{t('sportDuration.unit')}</small>
        </div>
      </div>
      <div className="sport-duration__selection" aria-live="polite">
        {active && <><span>{name(active.label)}</span><strong dir="ltr">{percent(active.value)}</strong><span dir="ltr">{duration(active.value)}</span></>}
      </div>
    </div>
  );
}
